/**
 * Customers-club benefit model + display helpers.
 *
 * A benefit is a small object: { type, ...typeSpecificFields }. The store
 * owner picks one of four types in the admin; the storefront renders a
 * human-readable description from the same shape.
 */

export const CLUB_BENEFIT_TYPES = [
    {
        id: 'coupon',
        label: 'קופון הנחה',
        hint: 'קופון עם אחוז הנחה ומספר שימושים',
    },
    {
        id: 'referral',
        label: 'קופון להעברה לחברים',
        hint: 'קופון שאפשר להעביר למספר אנשים — וצובר הנחה למזמין',
    },
    {
        id: 'punch_card',
        label: 'רכישה במתנה',
        hint: 'אחרי מספר רכישות, הרכישה הבאה במתנה',
    },
    {
        id: 'other',
        label: 'אחר',
        hint: 'הטבה חופשית שתתארו בעצמכם',
    },
];

const n = (v, fallback = 0) => {
    const num = Number(v);
    return Number.isFinite(num) ? num : fallback;
};

/** True when the benefit is configured enough to surface on the storefront. */
export function hasClubBenefit(benefit) {
    if (!benefit || typeof benefit !== 'object' || !benefit.type) return false;
    switch (benefit.type) {
        case 'coupon':
            return n(benefit.discount_percent) > 0;
        case 'referral':
            return n(benefit.referral_discount_percent) > 0 && n(benefit.max_uses) > 0;
        case 'punch_card':
            return n(benefit.purchases_required) >= 2;
        case 'other':
            return !!(benefit.description && benefit.description.trim());
        default:
            return false;
    }
}

/** Short one-line headline for the storefront CTA. */
export function clubBenefitHeadline(benefit) {
    if (!benefit || !benefit.type) return '';
    switch (benefit.type) {
        case 'coupon':
            return `קופון ${n(benefit.discount_percent)}% הנחה`;
        case 'referral':
            return `קופון להעברה לחברים — ${n(benefit.referral_discount_percent)}% על כל מימוש`;
        case 'punch_card':
            return `הרכישה ה-${n(benefit.purchases_required)} במתנה`;
        case 'other':
            return (benefit.description || '').trim();
        default:
            return '';
    }
}

/** Fuller description shown inside the registration modal. */
export function clubBenefitDescription(benefit) {
    if (!benefit || !benefit.type) return '';
    switch (benefit.type) {
        case 'coupon':
            return `קופון בהנחה של ${n(benefit.discount_percent)}%, עד ${n(benefit.max_uses)} שימושים.`;
        case 'referral':
            return `העבירו את הקופון לעד ${n(benefit.max_uses)} אנשים. בכל פעם שמישהו מממש אותו, תקבלו ${n(benefit.referral_discount_percent)}% הנחה — וההנחה מצטברת!`;
        case 'punch_card': {
            const x = n(benefit.purchases_required);
            const cap = n(benefit.max_free_value);
            const base = `אחרי ${Math.max(0, x - 1)} רכישות, הרכישה הבאה עלינו.`;
            return cap > 0 ? `${base} (עד ₪${cap})` : base;
        }
        case 'other':
            return (benefit.description || '').trim();
        default:
            return '';
    }
}
